@file:Suppress("ktlint:standard:max-line-length")

package com.royceecoleman.bill.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.royceecoleman.bill.shared.billing.BillingAudit
import com.royceecoleman.bill.shared.billing.BillingCodes
import com.royceecoleman.bill.shared.billing.BillingEntry
import com.royceecoleman.bill.shared.billing.BillingGuide
import com.royceecoleman.bill.shared.billing.BillingWorkspace
import com.royceecoleman.bill.shared.billing.ExpenseEntry
import com.royceecoleman.bill.shared.billing.InvoiceDetails
import com.royceecoleman.bill.shared.billing.SourceNote
import com.royceecoleman.bill.shared.billing.Timekeeper
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

/** Everything the review screen can ask the page to do; each action runs through the page's busy/error handling. */
internal class ReviewActions(
    val change: (BillingWorkspace) -> Unit,
    val save: () -> Unit,
    val reload: () -> Unit,
    val generate: () -> Unit,
    val approve: (String) -> Unit,
    val approveAll: () -> Unit,
    val redraft: (String, String) -> Unit,
    val saveExample: (String) -> Unit,
    val loadHistory: (onLoaded: (List<BillingAudit>) -> Unit) -> Unit,
    val export: (String) -> Unit,
    val delete: () -> Unit,
    val nextMonth: () -> Unit,
    val copyAll: () -> Unit = {},
)

private val casePrefix = Regex("""^\s*([^:\n]{1,60}?):\s""")

/** The case name a note starts with, as in "Rowley: R/R order", or null when the note has no such prefix. */
internal fun caseName(note: String): String? =
    casePrefix
        .find(note)
        ?.groupValues
        ?.get(1)
        ?.trim()
        ?.takeIf { it.isNotEmpty() && !it.contains("http", ignoreCase = true) }

/**
 * Every drafted entry as one numbered list for the clipboard, in page order. An entry whose note starts with a case
 * name gets the same prefix, unless its narrative already carries it (as a verbatim "don't bill" note does).
 */
internal fun entriesClipboardText(w: BillingWorkspace): String {
    val notes = w.notes.associateBy { it.id }
    return w.entries
        .filter { it.narrative.isNotBlank() }
        .mapIndexed { index, entry ->
            val narrative = entry.narrative.trim()
            val case = notes[entry.sourceId]?.text?.let(::caseName)
            val prefixed = if (case == null || narrative.contains("$case:", ignoreCase = true)) narrative else "$case: $narrative"
            "${index + 1}. $prefixed"
        }.joinToString("\n")
}

/** Next free numeric ID across notes and expenses, so added, split and expense rows never collide. */
internal fun nextItemId(
    w: BillingWorkspace,
    prefix: String = "",
): String {
    val used = (w.notes.map { it.id } + w.expenses.map { it.id }).toSet()
    var next = (used.mapNotNull { it.removePrefix(prefix).toIntOrNull() }.maxOrNull() ?: 0) + 1
    while ("$prefix$next" in used) next++
    return "$prefix$next"
}

/** Splits an entry into two rows sharing the note; the lawyer divides the recorded time. */
internal fun splitEntry(
    w: BillingWorkspace,
    sourceId: String,
): BillingWorkspace {
    val index = w.notes.indexOfFirst { it.id == sourceId }
    if (index < 0) return w
    val note = w.notes[index]
    val entry = w.entries.first { it.sourceId == sourceId }
    val id = nextItemId(w)
    return w.copy(
        notes = w.notes.toMutableList().apply { add(index + 1, note.copy(id = id, minutes = null)) },
        entries =
            w.entries.toMutableList().apply {
                set(index, entry.copy(approved = false))
                add(index + 1, BillingEntry(id, date = w.invoice.effectiveDate(entry.date), timekeeperId = entry.timekeeperId))
            },
    )
}

/** Merges an entry with the one after it: notes and narratives are joined and recorded minutes added. */
internal fun mergeWithNext(
    w: BillingWorkspace,
    sourceId: String,
): BillingWorkspace {
    val index = w.notes.indexOfFirst { it.id == sourceId }
    if (index < 0 || index + 1 >= w.notes.size) return w
    val (first, second) = w.notes[index] to w.notes[index + 1]
    val (a, b) = w.entries[index] to w.entries[index + 1]
    val minutes = if (a.minutes != null && b.minutes != null) a.minutes!! + b.minutes!! else a.minutes ?: b.minutes
    return w.copy(
        notes = w.notes.filterIndexed { i, _ -> i != index + 1 }.map { if (it.id == first.id) it.copy(text = "${first.text}; ${second.text}", minutes = minutes) else it },
        entries =
            w.entries.filterIndexed { i, _ -> i != index + 1 }.map {
                if (it.sourceId == a.sourceId) a.copy(narrative = listOf(a.narrative, b.narrative).filter(String::isNotBlank).joinToString(" "), minutes = minutes, approved = false) else it
            },
    )
}

internal fun removeEntry(
    w: BillingWorkspace,
    sourceId: String,
) = w.copy(notes = w.notes.filterNot { it.id == sourceId }, entries = w.entries.filterNot { it.sourceId == sourceId })

/** Adds a row dated with the shared work date, if any; otherwise the lawyer enters the date (or leaves it blank). */
internal fun addEntry(w: BillingWorkspace): BillingWorkspace {
    val id = nextItemId(w)
    return w.copy(notes = w.notes + SourceNote(id, "Added work"), entries = w.entries + BillingEntry(id, date = w.invoice.effectiveDate("")))
}

/** Changes the invoice; a new shared work date also moves entries that were using the old one and have no date of their own. */
internal fun changeInvoice(
    w: BillingWorkspace,
    invoice: InvoiceDetails,
): BillingWorkspace {
    val old = w.invoice.workDate
    if (invoice.workDate == old) return w.copy(invoice = invoice)
    val undated = w.notes.filter { it.date.isBlank() }.map { it.id }.toSet()
    return w.copy(
        invoice = invoice,
        entries =
            w.entries.map {
                if (it.sourceId in undated && (it.date.isBlank() || it.date == old)) it.copy(date = invoice.workDate, approved = false) else it
            },
    )
}

@Composable
internal fun ReviewWorkspace(
    w: BillingWorkspace,
    latest: () -> BillingWorkspace,
    busy: Boolean,
    dirty: Boolean,
    actions: ReviewActions,
) {
    val generating = w.status == "GENERATING"
    val editable = !generating && w.entries.isNotEmpty()
    Eyebrow("04–05 / REVIEW, APPROVE & EXPORT")
    H1 { Text(w.title) }
    GuideSteps(if (w.status == "APPROVED" && !dirty) 5 else 4)
    Div({
        classes("bill-alert", "bill-notice")
        attr("role", "status")
    }) {
        H3 { Text("Next step") }
        P { Text(BillingGuide.next(w, dirty)) }
        val firstIssue = w.issues.firstOrNull { it.blocking }
        val firstEntry = firstIssue?.sourceId?.ifEmpty { null } ?: w.entries.firstOrNull { !it.approved }?.sourceId
        val target =
            when {
                dirty || w.entries.isEmpty() -> "bill-review-actions"
                firstIssue != null && firstIssue.sourceId.isEmpty() -> "bill-invoice"
                else -> firstEntry?.let { "bill-entry-$it" } ?: "bill-export"
            }
        if (!generating) BillButton("Show me where", busy) { focusBillingSection(target) }
    }
    Div({ classes("bill-row", "bill-between") }) {
        P({ classes("bill-muted") }) { Text("${w.invoice.month} · ${w.profile.name} v${w.profile.revision} · ${w.notes.size} notes · ${w.expenses.size} expenses") }
        Span({ classes("bill-pill") }) { Text(if (dirty) "UNSAVED EDITS" else w.status) }
    }
    if (generating) {
        val percent = if (w.progress.total == 0) 0 else w.progress.drafted * 100 / w.progress.total
        Div({ classes("bill-card") }) {
            H3 {
                Text(
                    if (w.progress.reviewing) {
                        "Checking ${w.progress.drafted} drafts against your rules…"
                    } else {
                        "Drafting ${w.progress.drafted} of ${w.progress.total} notes…"
                    },
                )
            }
            Div({
                classes("bill-progress")
                attr("role", "progressbar")
                attr("aria-valuenow", percent.toString())
                attr("aria-valuemin", "0")
                attr("aria-valuemax", "100")
            }) { Div({ style { property("width", "$percent%") } }) }
            P({ classes("bill-muted") }) { Text("Drafting continues if you leave this page. Notes that cannot be drafted are not charged.") }
        }
    }
    if (w.error.isNotBlank()) {
        P({
            classes("bill-alert")
            attr("role", "alert")
        }) { Text(w.error) }
    }
    if (w.styleSummary.isNotBlank()) {
        Div({ classes("bill-card") }) {
            H3 { Text("The writing patterns BILL inferred") }
            P { Text(w.styleSummary) }
            Text("Drafted with ${w.modelId} · ${w.inputTokens + w.outputTokens} reported tokens")
        }
    }
    Div({
        classes("bill-card")
        id("bill-invoice")
        attr("tabindex", "-1")
    }) {
        H2 { Text("Matter & invoice details") }
        InvoiceEditor(w.invoice, { latest().invoice }) { actions.change(changeInvoice(latest(), it)) }
        BillDetails("Invoice & LEDES details") { LedesEditor(w.invoice, { latest().invoice }) { actions.change(latest().copy(invoice = it)) } }
        BillDetails("Other timekeepers on this invoice (${w.invoice.additionalTimekeepers.size})") {
            TimekeepersEditor(w.invoice.additionalTimekeepers, { latest().invoice.additionalTimekeepers }) {
                actions.change(latest().copy(invoice = latest().invoice.copy(additionalTimekeepers = it)))
            }
        }
        val invoiceIssues = w.issues.filter { it.sourceId.isEmpty() }
        if (invoiceIssues.isNotEmpty()) Ul({ classes("bill-issues") }) { invoiceIssues.forEach { Li { Text("${if (it.blocking) "Fix" else "Check"}: ${it.message}") } } }
        P({ classes("bill-muted") }) { Text("Checks reflect the last saved version. Changing invoice details keeps entry approvals; changing an entry clears its approval.") }
    }
    val invoiceBlocked = w.issues.any { it.sourceId.isEmpty() && it.blocking }
    val blockedIds = w.issues.filter { it.blocking }.map { it.sourceId }.toSet()
    val approvable = (w.entries.filterNot { it.approved }.map { it.sourceId } + w.expenses.filterNot { it.approved }.map { it.id }).count { it !in blockedIds }
    Div({
        classes("bill-row")
        id("bill-review-actions")
        attr("tabindex", "-1")
    }) {
        if (w.status in listOf("DRAFT", "FAILED")) {
            BillButton(if (w.status == "FAILED") "Retry AI draft (up to ${w.notes.size} credits)" else "Draft with AI (up to ${w.notes.size} credits)", busy || dirty, "primary", actions.generate)
        }
        BillButton("Save edits", busy || generating || !dirty, "primary", actions.save)
        BillButton("Reload saved version", busy, "", actions.reload)
        if (w.entries.any { it.narrative.isNotBlank() } && !generating) {
            BillButton("Copy all entries", busy, "", actions.copyAll)
        }
        if (w.entries.isEmpty() && !generating) {
            BillButton("Write entries manually", busy) {
                val current = latest()
                actions.change(current.copy(entries = current.notes.map { BillingEntry(it.id, date = current.invoice.effectiveDate(it.date), minutes = it.minutes, timekeeperId = it.timekeeperId) }))
            }
        }
        if (editable) {
            BillButton("Approve all clean entries ($approvable)", busy || dirty || invoiceBlocked || approvable == 0, "", actions.approveAll)
            BillButton("+ Add entry", busy) { actions.change(addEntry(latest())) }
        }
    }
    if (w.entries.isEmpty()) {
        // The batch waiting to be drafted: notes can still be dropped, so they are neither drafted nor charged.
        if (!generating) {
            P({ classes("bill-muted") }) {
                Text("Remove any notes you don't want drafted, then save your edits before drafting. Each remaining note uses up to one credit.")
            }
        }
        w.notes.forEach { note ->
            key(w.id, note.id) {
                Div({ classes("bill-card") }) {
                    H3 { Text("Source ${note.id}") }
                    P({ classes("bill-source") }) { Text(note.text) }
                    Text("${w.invoice.effectiveDate(note.date).ifEmpty { "No date" }} · ${note.minutes?.let { "$it minutes" } ?: "No time recorded"}")
                    if (!generating) {
                        Div({ classes("bill-row") }) {
                            // A workspace keeps at least one note; delete the workspace to drop them all.
                            BillButton("Remove source ${note.id}", busy || w.notes.size <= 1, "danger") {
                                actions.change(removeEntry(latest(), note.id))
                            }
                        }
                    }
                }
            }
        }
    }
    w.entries.forEachIndexed { index, entry ->
        key(w.id, entry.sourceId) {
            EntryCard(w, entry, index == w.entries.lastIndex, latest, busy, dirty, generating, actions)
        }
    }
    if (editable || w.expenses.isNotEmpty()) ExpensesSection(w, latest, busy, dirty, generating, actions)
    Div({
        classes("bill-card")
        id("bill-export")
        attr("tabindex", "-1")
    }) {
        H2 { Text("Ready for your billing software.") }
        var format by remember { mutableStateOf("csv") }
        BillSelect(
            "Export format",
            format,
            mapOf(
                "csv" to "CSV — map columns in your billing software",
                "ledes" to "LEDES 1998B — USD fees and expenses",
            ),
        ) { format = it }
        P {
            Text(
                "${w.entries.count { it.approved }} of ${w.entries.size} entries and ${w.expenses.count { it.approved }} of ${w.expenses.size} expenses approved. Export includes only your reviewed billing fields, not the rough notes or examples.",
            )
        }
        // CSV leaves a missing date or time blank; LEDES cannot, so its button explains what is missing.
        val incomplete = w.incompleteEntries()
        Div({ classes("bill-row") }) {
            val disabled = busy || dirty || w.status != "APPROVED" || (format == "ledes" && incomplete.isNotEmpty())
            BillButton(if (format == "csv") "Download CSV" else "Download LEDES 1998B", disabled, "primary") { actions.export(format) }
        }
        if (format == "ledes" && incomplete.isNotEmpty()) {
            P({
                classes("bill-alert", "bill-notice")
                attr("role", "status")
            }) {
                Text("LEDES needs a work date and actual time on every entry. Missing: ${incomplete.joinToString("; ")}. Add them and save, or download CSV, which leaves them blank.")
            }
        }
        P({ classes("bill-muted") }) {
            Text(
                "CSV keeps its original columns first and adds type, timekeeper name, gross, adjustment, expense code and no-charge columns at the end${if (w.profile.includeCodes) "" else "; this profile has no task or activity code columns"}. Entries without time export blank hours and amount. LEDES needs a date and time on every entry, the invoice details above and each timekeeper's name (Last, First) and classification. Client acceptance still depends on their rules.",
            )
        }
    }
    var history by remember(w.id) { mutableStateOf<List<BillingAudit>?>(null) }
    BillDetails("Review history (${history?.size ?: w.audit.size} events)") {
        if (history == null) BillButton("Load the complete history", busy) { actions.loadHistory { history = it } }
        Ul { (history ?: w.audit).asReversed().forEach { Li { Text("${kotlin.js.Date(it.timestamp.toDouble()).toISOString()} — ${it.action}") } } }
    }
    BillButton("Delete this workspace", busy || generating, "danger", actions.delete)
    BillButton("Start another month with these details", busy || generating, "", actions.nextMonth)
}

@Composable
private fun EntryCard(
    w: BillingWorkspace,
    entry: BillingEntry,
    last: Boolean,
    latest: () -> BillingWorkspace,
    busy: Boolean,
    dirty: Boolean,
    generating: Boolean,
    actions: ReviewActions,
) {
    val note = w.notes.firstOrNull { it.id == entry.sourceId } ?: SourceNote(entry.sourceId)
    val issues = w.issues.filter { it.sourceId == entry.sourceId }
    val saved = !dirty && !generating

    fun currentEntry() = latest().entries.first { it.sourceId == entry.sourceId }

    fun update(e: BillingEntry) = actions.change(latest().copy(entries = latest().entries.map { if (it.sourceId == e.sourceId) e.copy(approved = false) else it }))

    fun updateNote(n: SourceNote) =
        actions.change(
            latest().copy(
                notes = latest().notes.map { if (it.id == n.id) n else it },
                entries = latest().entries.map { if (it.sourceId == n.id) it.copy(approved = false) else it },
            ),
        )
    Div({
        classes("bill-card")
        id("bill-entry-${entry.sourceId}")
        attr("tabindex", "-1")
    }) {
        Div({ classes("bill-row", "bill-between") }) {
            H3 { Text("Entry ${entry.sourceId}") }
            Span({
                classes("bill-pill")
                if (!entry.approved) classes("warning")
            }) { Text(if (entry.approved) "APPROVED" else if (entry.noCharge) "NO CHARGE · REVIEW REQUIRED" else "REVIEW REQUIRED") }
        }
        Div({ classes("bill-two") }) {
            Div {
                Eyebrow("ORIGINAL NOTE")
                P({ classes("bill-source") }) { Text(note.text) }
                BillDetails("Edit the original note") {
                    BillArea("Original note", note.text) { value -> latest().notes.firstOrNull { it.id == note.id }?.let { updateNote(it.copy(text = value)) } }
                }
            }
            Div {
                BillArea("Billing narrative", entry.narrative) { update(currentEntry().copy(narrative = it)) }
                Div({ classes("bill-two") }) {
                    BillField("Work date (YYYY-MM-DD)", entry.date, w.invoice.workDate.ifBlank { "Optional" }) { update(currentEntry().copy(date = it)) }
                    BillField("Actual minutes", entry.minutes?.toString().orEmpty(), "Optional") { update(currentEntry().copy(minutes = it.trim().toIntOrNull())) }
                    if (w.profile.includeCodes) {
                        BillSelect("Task code", entry.taskCode, BillingCodes.tasks.filterKeys { it in w.profile.taskCodes }, true) { update(currentEntry().copy(taskCode = it)) }
                        BillSelect("Activity code", entry.activityCode, BillingCodes.activities.filterKeys { it in w.profile.activityCodes }, true) { update(currentEntry().copy(activityCode = it)) }
                    }
                    BillSelect("Timekeeper", entry.timekeeperId, timekeeperOptions(w.invoice)) { update(currentEntry().copy(timekeeperId = if (it == w.invoice.timekeeperId) "" else it)) }
                    BillField("Write-down amount (optional)", entry.discount, "e.g. 50.00") { update(currentEntry().copy(discount = it.trim())) }
                }
                BillCheck("No charge — show on the invoice at zero", entry.noCharge) { update(currentEntry().copy(noCharge = it)) }
            }
        }
        if (issues.isNotEmpty()) {
            Ul({ classes("bill-issues") }) {
                issues.forEach { issue ->
                    Li {
                        Text("${if (issue.blocking) "Fix" else "Check"}: ${issue.message}")
                        // Fixes are computed on the saved narrative, so they are offered only when there are no unsaved edits.
                        if (issue.fix.isNotEmpty()) {
                            Text(" ")
                            BillButton(issue.fixLabel.ifBlank { "Apply fix" }, busy || !saved) { update(currentEntry().copy(narrative = issue.fix)) }
                        }
                    }
                }
            }
        }
        P({ classes("bill-muted") }) {
            Text("Approval confirms you checked the original facts, actual time${if (w.profile.includeCodes) ", suggested codes" else ""}, warnings and client billing guidelines.")
        }
        Div({ classes("bill-row") }) {
            BillButton(
                "Approve reviewed entry",
                busy || dirty || entry.approved || issues.any { it.blocking } || w.issues.any { it.sourceId.isEmpty() && it.blocking },
                "",
            ) { actions.approve(entry.sourceId) }
            BillButton("Split", busy || generating) { actions.change(splitEntry(latest(), entry.sourceId)) }
            BillButton("Merge with next", busy || generating || last) { actions.change(mergeWithNext(latest(), entry.sourceId)) }
            BillButton("Delete", busy || generating || w.entries.size <= 1, "danger") { actions.change(removeEntry(latest(), entry.sourceId)) }
            BillButton("Save as example", busy || !saved || entry.narrative.isBlank()) { actions.saveExample(entry.sourceId) }
        }
        if (w.status in listOf("REVIEW", "APPROVED")) {
            var hint by remember(entry.sourceId) { mutableStateOf("") }
            BillDetails("Redraft with AI (1 credit if a draft is returned)") {
                BillField("Optional hint", hint, "e.g. mention the deposition date; shorter") { hint = it }
                BillButton("Redraft this entry", busy || !saved) { actions.redraft(entry.sourceId, hint) }
                P({ classes("bill-muted") }) {
                    Text("Replaces the narrative${if (w.profile.includeCodes) " and codes" else ""} only. Your time, date, timekeeper and adjustments stay as you entered them. Save edits first.")
                }
            }
        }
    }
}

@Composable
private fun ExpensesSection(
    w: BillingWorkspace,
    latest: () -> BillingWorkspace,
    busy: Boolean,
    dirty: Boolean,
    generating: Boolean,
    actions: ReviewActions,
) {
    fun update(expense: ExpenseEntry) =
        actions.change(latest().copy(expenses = latest().expenses.map { if (it.id == expense.id) expense.copy(approved = false) else it }))
    Div({ classes("bill-card") }) {
        H2 { Text("Expenses") }
        P({ classes("bill-muted") }) { Text("Add disbursements with ABA expense codes. Expenses are never drafted by AI.") }
        latest().expenses.forEach { expense ->
            key(expense.id) {
                val issues = w.issues.filter { it.sourceId == expense.id }
                Div({ classes("bill-card") }) {
                    Div({ classes("bill-row", "bill-between") }) {
                        H3 { Text("Expense ${expense.id}") }
                        Span({
                            classes("bill-pill")
                            if (!expense.approved) classes("warning")
                        }) { Text(if (expense.approved) "APPROVED" else "REVIEW REQUIRED") }
                    }
                    Div({ classes("bill-grid") }) {
                        BillField("Date (YYYY-MM-DD)", expense.date) { update(expense.copy(date = it)) }
                        BillField("Description", expense.description) { update(expense.copy(description = it)) }
                        BillSelect("Expense code", expense.expenseCode, BillingCodes.expenses, true) { update(expense.copy(expenseCode = it)) }
                        BillField("Amount", expense.amount, "e.g. 400.00") { update(expense.copy(amount = it.trim())) }
                    }
                    if (issues.isNotEmpty()) Ul({ classes("bill-issues") }) { issues.forEach { Li { Text("${if (it.blocking) "Fix" else "Check"}: ${it.message}") } } }
                    Div({ classes("bill-row") }) {
                        BillButton("Approve expense", busy || dirty || expense.approved || issues.any { it.blocking }) { actions.approve(expense.id) }
                        BillButton("Remove", busy || generating, "danger") { actions.change(latest().copy(expenses = latest().expenses.filterNot { it.id == expense.id })) }
                    }
                }
            }
        }
        BillButton("+ Add expense", busy || generating) {
            val current = latest()
            actions.change(current.copy(expenses = current.expenses + ExpenseEntry(nextItemId(current, "x"), date = current.invoice.month.takeIf { it.length == 7 }?.let { "$it-01" }.orEmpty())))
        }
    }
}

private fun timekeeperOptions(invoice: InvoiceDetails): Map<String, String> =
    linkedMapOf(invoice.timekeeperId to "${invoice.timekeeperId.ifBlank { "Default" }} — ${invoice.timekeeperName.ifBlank { "default timekeeper" }}") +
        invoice.additionalTimekeepers.associate { it.id to "${it.id} — ${it.name}" }

@Composable
internal fun InvoiceEditor(
    i: InvoiceDetails,
    latest: () -> InvoiceDetails,
    showMonth: Boolean = true,
    change: (InvoiceDetails) -> Unit,
) {
    Div({ classes("bill-grid") }) {
        if (showMonth) BillField("Billing month (YYYY-MM)", i.month, "2026-09") { change(latest().copy(month = it)) }
        if (showMonth) BillField("Shared work date (optional, YYYY-MM-DD)", i.workDate, "When all sources are from one day") { change(latest().copy(workDate = it.trim())) }
        BillField("Client ID", i.clientId) { change(latest().copy(clientId = it)) }
        BillField("Firm matter ID", i.matterId) { change(latest().copy(matterId = it)) }
        BillField("Timekeeper ID", i.timekeeperId) { change(latest().copy(timekeeperId = it)) }
        BillField("Timekeeper name (Last, First)", i.timekeeperName) { change(latest().copy(timekeeperName = it)) }
        BillField("Agreed hourly rate", i.hourlyRate, "e.g. 350.00") { change(latest().copy(hourlyRate = it)) }
        BillField("Currency", i.currency) { change(latest().copy(currency = it.uppercase())) }
    }
    P({ classes("bill-muted") }) { Text("Use IDs from your billing software and the agreed rate, not guesses.") }
}

@Composable
internal fun LedesEditor(
    i: InvoiceDetails,
    latest: () -> InvoiceDetails,
    change: (InvoiceDetails) -> Unit,
) {
    Div({ classes("bill-grid") }) {
        BillField("Invoice number", i.invoiceNumber) { change(latest().copy(invoiceNumber = it)) }
        BillField("Invoice date (YYYY-MM-DD)", i.invoiceDate) { change(latest().copy(invoiceDate = it)) }
        BillField("Law firm ID / tax ID", i.firmId) { change(latest().copy(firmId = it)) }
        BillField("Client matter ID", i.clientMatterId) { change(latest().copy(clientMatterId = it)) }
        BillField("Timekeeper classification", i.timekeeperClassification, "e.g. AS") { change(latest().copy(timekeeperClassification = it)) }
        BillField("Invoice description", i.description, "Professional services rendered …") { change(latest().copy(description = it)) }
    }
    BillCheck("This client does not assign its own matter IDs", i.clientHasNoMatterId) { change(latest().copy(clientHasNoMatterId = it)) }
}

@Composable
internal fun TimekeepersEditor(
    list: List<Timekeeper>,
    latest: () -> List<Timekeeper>,
    change: (List<Timekeeper>) -> Unit,
) {
    fun update(
        index: Int,
        keeper: Timekeeper,
    ) = change(latest().mapIndexed { i, it -> if (i == index) keeper else it })
    list.forEachIndexed { index, keeper ->
        Div({ classes("bill-grid") }) {
            BillField("ID", keeper.id) { update(index, latest()[index].copy(id = it.trim())) }
            BillField("Name (Last, First)", keeper.name) { update(index, latest()[index].copy(name = it)) }
            BillField("Classification", keeper.classification, "e.g. PT") { update(index, latest()[index].copy(classification = it)) }
            BillField("Hourly rate", keeper.hourlyRate) { update(index, latest()[index].copy(hourlyRate = it.trim())) }
            BillButton("Remove", LocalWorkbenchBusy.current, "danger") { change(latest().filterIndexed { i, _ -> i != index }) }
        }
    }
    BillButton("+ Add timekeeper", LocalWorkbenchBusy.current || list.size >= 20) { change(latest() + Timekeeper()) }
}
